/**
 * TechMap — per-user technology-estate canvas for one account (Tech Map).
 *
 * Seeded server-side from the caller's visible notes (products/competitors
 * + note evidence); the user then drags, re-zones, tags, and annotates.
 * State persists per (account, owner) via /api/v1/tech-map — owner-private,
 * because the seed may embed private-note intel. GAS renders the same map
 * read-only.
 *
 * Reads via useQuery, writes via useMutation (DEVELOPMENT_RULES rule 3);
 * saves are debounced — every edit marks dirty, a 1.5s timer flushes.
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Plus, X, Save } from 'lucide-react';
import { confirmDialog, promptDialog } from '../ConfirmDialog';
import apiClient from '../../lib/apiClient';
import './TechMap.css';

const VENDOR_CLASS = (n) => {
  if (n.status === 'competitor') return 'tm-competitor';
  if (n.status === 'gap') return 'tm-gap';
  if (n.status === 'target') return 'tm-pan';
  const v = (n.vendor || '').toLowerCase();
  if (v.includes('cisco')) return 'tm-cisco';
  if (v.includes('palo alto')) return 'tm-pan';
  return 'tm-other';
};

export default function TechMap({ account }) {
  const queryClient = useQueryClient();
  const base = `/api/v1/tech-map/${encodeURIComponent(account)}`;

  const { data, isLoading, error } = useQuery({
    queryKey: ['tech-map', account],
    queryFn: () => apiClient.get(base),
    staleTime: 60_000,
  });

  const [state, setState] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [sel, setSel] = useState(null);
  const [filter, setFilter] = useState('all');
  const stateRef = useRef(state);
  stateRef.current = state;
  const canvasRef = useRef(null);
  const svgRef = useRef(null);

  // Node visibility under the active filter — nodes dim, edges follow.
  const nodeShown = useCallback((n) => {
    if (filter === 'all') return true;
    if (filter === 'pan') return n.status === 'target' || /palo alto/i.test(n.vendor || '');
    if (filter === 'competitor') return n.status === 'competitor';
    if (filter === 'gap') return n.status === 'gap';
    if (filter === 'cisco') return /cisco/i.test(n.vendor || '');
    return true;
  }, [filter]);

  // Edges are drawn imperatively into an SVG overlay spanning all zones —
  // measured from the DOM so lines track nodes live during a drag.
  const drawEdges = useCallback(() => {
    const canvas = canvasRef.current, svg = svgRef.current;
    const s = stateRef.current;
    if (!canvas || !svg || !s) return;
    svg.setAttribute('viewBox', `0 0 ${canvas.offsetWidth} ${canvas.offsetHeight}`);
    svg.setAttribute('width', canvas.offsetWidth);
    svg.setAttribute('height', canvas.offsetHeight);
    const center = (id) => {
      const el = canvas.querySelector(`[data-nid="${CSS.escape(id)}"]`);
      if (!el) return null;
      const zone = el.parentElement;
      return {
        x: zone.offsetLeft + el.offsetLeft + el.offsetWidth / 2,
        y: zone.offsetTop + el.offsetTop + el.offsetHeight / 2,
        dim: el.classList.contains('tm-dim'),
      };
    };
    svg.innerHTML = (s.edges || []).map(([a, b, w]) => {
      const A = center(a), B = center(b);
      if (!A || !B) return '';
      const op = (A.dim || B.dim) ? 0.05 : Math.min(0.16 + (w || 1) * 0.07, 0.5);
      const sw = 1 + Math.min(w || 1, 4) * 0.35;
      return `<line x1="${A.x}" y1="${A.y}" x2="${B.x}" y2="${B.y}" stroke="currentColor" stroke-width="${sw}" opacity="${op}"/>`;
    }).join('');
  }, []);

  useLayoutEffect(() => { drawEdges(); }, [state, filter, drawEdges]);
  useEffect(() => {
    window.addEventListener('resize', drawEdges);
    return () => window.removeEventListener('resize', drawEdges);
  }, [drawEdges]);

  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => {
    // Never clobber in-progress edits with a background refetch.
    if (data?.state && !dirtyRef.current) { setState(data.state); setDirty(false); }
  }, [data]);

  // While the server is annotating in the background, poll until done.
  useEffect(() => {
    if (!data?.state?.annotating || dirtyRef.current) return;
    const t = setTimeout(
      () => queryClient.invalidateQueries({ queryKey: ['tech-map', account] }),
      6000,
    );
    return () => clearTimeout(t);
  }, [data, account, queryClient]);

  const saveMutation = useMutation({
    mutationFn: (s) => apiClient.put(base, { state: s }),
    onSuccess: () => setDirty(false),
  });

  // Debounced autosave: any edit marks dirty; flush 1.5s after the last one.
  const saveTimer = useRef(null);
  const touch = useCallback((updater) => {
    setState((s) => {
      const next = typeof updater === 'function' ? updater(s) : updater;
      return next;
    });
    setDirty(true);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      if (stateRef.current) saveMutation.mutate(stateRef.current);
    }, 1500);
  }, [saveMutation]);
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const reseed = async () => {
    const fresh = await apiClient.post(`${base}/reseed`, {});
    touch(fresh.state);
    setSel(null);
  };

  // ── drag within a zone box ────────────────────────────────────────────
  const drag = useRef(null);
  const onPointerDown = (e, nodeId) => {
    const el = e.currentTarget;
    drag.current = {
      nodeId,
      startX: e.clientX, startY: e.clientY,
      origX: el.offsetLeft, origY: el.offsetTop,
      moved: false,
    };
    el.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.startX, dy = e.clientY - d.startY;
    if (Math.abs(dx) + Math.abs(dy) > 3) d.moved = true;
    if (!d.moved) return;
    const zoneEl = e.currentTarget.parentElement;
    const x = Math.max(0, Math.min(zoneEl.clientWidth - 130, d.origX + dx));
    const y = Math.max(24, Math.min(zoneEl.clientHeight - 40, d.origY + dy));
    e.currentTarget.style.left = `${x}px`;
    e.currentTarget.style.top = `${y}px`;
    drawEdges();
  };
  const onPointerUp = (e, nodeId) => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (!d.moved) { setSel(nodeId); return; }
    const x = e.currentTarget.offsetLeft, y = e.currentTarget.offsetTop;
    touch((s) => ({
      ...s,
      nodes: s.nodes.map((n) => (n.id === nodeId ? { ...n, x, y } : n)),
    }));
  };

  // ── node edits from the detail card ───────────────────────────────────
  const patchNode = (id, patch) =>
    touch((s) => ({ ...s, nodes: s.nodes.map((n) => (n.id === id ? { ...n, ...patch } : n)) }));
  const removeNode = (id) => {
    touch((s) => ({ ...s, nodes: s.nodes.filter((n) => n.id !== id) }));
    setSel(null);
  };
  const addNode = async () => {
    const name = await promptDialog({
      title: 'Add node',
      placeholder: 'Technology / product name',
      confirmLabel: 'Add',
    });
    if (!name?.trim()) return;
    const id = `${name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now() % 10000}`;
    touch((s) => ({
      ...s,
      nodes: [...s.nodes, {
        id, zone: 'other', x: 20, y: 40, name: name.trim(),
        vendor: null, status: 'estate', mentions: 0, tags: [], evidence: [],
      }],
    }));
    setSel(id);
  };

  if (isLoading) return <div className="tm-empty">Loading tech map…</div>;
  if (error) return <div className="tm-empty">Couldn't load the tech map: {error.message}</div>;
  if (!state) return null;

  const zones = state.zones || [];
  const selNode = sel ? state.nodes.find((n) => n.id === sel) : null;
  const zoneHeight = (z) => {
    const ys = state.nodes.filter((n) => n.zone === z.id).map((n) => n.y);
    return Math.max(120, (ys.length ? Math.max(...ys) : 0) + 70);
  };

  return (
    <div className="tm-root">
      <div className="tm-toolbar">
        <span className="tm-hint">
          {data?.seeded && !dirty
            ? `Seeded from ${state.noteCount ?? '—'} notes — drag, tag, edit; it saves as your private map.`
            : 'Drag nodes · click for evidence · edits autosave.'}
        </span>
        {state.annotating > 0 && (
          <span className="tm-annotating">✦ AI annotating {state.annotating} node{state.annotating > 1 ? 's' : ''}…</span>
        )}
        <span className={`tm-savestate ${dirty ? 'tm-dirty' : ''}`}>
          {saveMutation.isPending ? 'Saving…' : dirty ? 'Unsaved edits' : 'Saved'}
        </span>
        <button className="tm-btn" onClick={addNode}><Plus size={13} /> Add node</button>
        <button className="tm-btn" onClick={() => saveMutation.mutate(stateRef.current)} disabled={!dirty}>
          <Save size={13} /> Save now
        </button>
        <button
          className="tm-btn"
          title="Rebuild the map from your current notes (replaces layout and node set; your save happens on next edit)"
          onClick={async () => {
            const ok = await confirmDialog({
              title: 'Re-seed from notes',
              message: 'This replaces the current node set and layout.',
              confirmLabel: 'Re-seed',
            });
            if (ok) reseed();
          }}
        >
          <RefreshCw size={13} /> Re-seed from notes
        </button>
      </div>

      <div className="tm-legend">
        <span className="tm-k tm-cisco">incumbent (Cisco)</span>
        <span className="tm-k tm-pan">Palo Alto / wedge</span>
        <span className="tm-k tm-competitor">competitor mention</span>
        <span className="tm-k tm-other">third-party / other</span>
        <span className="tm-k-edges">line = co-mentioned in 2+ notes (thicker = more)</span>
      </div>

      <div className="tm-filters">
        {[['all', 'All'], ['pan', 'PAN wedge'], ['competitor', 'Competitors'], ['cisco', 'Cisco estate'], ['gap', 'Gaps']].map(([k, label]) => (
          <button
            key={k}
            className={`tm-chip ${filter === k ? 'tm-chip-on' : ''} ${k === 'pan' ? 'tm-chip-pan' : ''}`}
            onClick={() => setFilter(k)}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="tm-body">
        <div className="tm-canvas" ref={canvasRef}>
          <svg className="tm-edges" ref={svgRef} aria-hidden="true" />
          {zones.map((z) => (
            <div key={z.id} className="tm-zone" style={{ minHeight: zoneHeight(z) }}>
              <span className="tm-zone-label">{z.label}</span>
              {state.nodes.filter((n) => n.zone === z.id).map((n) => (
                <div
                  key={n.id}
                  data-nid={n.id}
                  className={`tm-node ${VENDOR_CLASS(n)} ${sel === n.id ? 'tm-sel' : ''} ${nodeShown(n) ? '' : 'tm-dim'}`}
                  style={{ left: n.x, top: n.y }}
                  onPointerDown={(e) => onPointerDown(e, n.id)}
                  onPointerMove={onPointerMove}
                  onPointerUp={(e) => onPointerUp(e, n.id)}
                >
                  <span className="tm-node-name">{n.name}</span>
                  {n.mentions > 1 && <span className="tm-node-badge">{n.mentions}×</span>}
                  {(n.tags || []).length > 0 && (
                    <span className="tm-node-tags">{n.tags.join(' · ')}</span>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {selNode && (
          <div className="tm-detail">
            <button className="tm-detail-close" onClick={() => setSel(null)}><X size={14} /></button>
            <h4>{selNode.name}</h4>
            <div className="tm-detail-meta">
              {selNode.vendor || 'vendor unknown'}
              {selNode.mentions > 0 && ` · mentioned in ${selNode.mentions} note${selNode.mentions > 1 ? 's' : ''}`}
            </div>

            {selNode.insight && <p className="tm-insight">{selNode.insight}</p>}
            {selNode.angle && <div className="tm-angle">{selNode.angle}</div>}

            <label>Status
              <select
                value={selNode.status}
                onChange={(e) => patchNode(selNode.id, { status: e.target.value })}
              >
                <option value="estate">In their estate</option>
                <option value="competitor">Competitor mention</option>
                <option value="target">PAN target</option>
                <option value="gap">Gap / risk</option>
              </select>
            </label>
            <label>Zone
              <select
                value={selNode.zone}
                onChange={(e) => patchNode(selNode.id, { zone: e.target.value, x: 20, y: 40 })}
              >
                {zones.map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
              </select>
            </label>

            <label>Tags</label>
            <div className="tm-tagrow">
              {(selNode.tags || []).map((t, i) => (
                <em key={i}>{t}
                  <button onClick={() => patchNode(selNode.id, { tags: selNode.tags.filter((_, j) => j !== i) })}>✕</button>
                </em>
              ))}
            </div>
            <input
              placeholder="add tag, press Enter"
              onKeyDown={(e) => {
                if (e.key !== 'Enter' || !e.target.value.trim()) return;
                patchNode(selNode.id, { tags: [...(selNode.tags || []), e.target.value.trim()] });
                e.target.value = '';
              }}
            />

            {(selNode.evidence || []).length > 0 && (
              <>
                <label>Evidence (from your notes)</label>
                <ul className="tm-evidence">
                  {selNode.evidence.map((ev, i) => (
                    <li key={i}><span>{ev.date}</span> {ev.title}</li>
                  ))}
                </ul>
              </>
            )}

            <button className="tm-btn tm-danger" onClick={() => removeNode(selNode.id)}>
              Remove node
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
